import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../../store/store';
import { Cycle } from '../../../../../store/models/cycles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../../../../constants/route-constants';
import { useAbilityContext } from '../../../../../components/Can/Can';
import { Action } from '../../../../../enums/action';
import { PAGE_TITLE } from '../../../../../constants/common';
import { IActionsDropdown, IActionsTag } from '../../../../../types/tableDropdown';
import IconEditOutlined from '../../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../../../types/element-size';
import IconBin from '../../../../../components/Icons/IconBin/IconBin';
import IconCopy from '../../../../../components/Icons/IconCopy/IconCopy';
import IconSwitcher from '../../../../../components/Icons/IconSwitcher/IconSwitcher';
import CycleStatus from '../../../../../enums/cycle-status.enum';
// import { usePolling } from '../../../../../hooks/usePolling';
import { Paperclip } from '../../../../../components/Icons/Paperclip/Paperclip';
import { OpenReport } from '../../../../../components/Icons/OpenReport/OpenReport';

import { useTranslation } from 'react-i18next';
import { IInactiveCycleContract } from '../interfaces/hook';
import { joinActionGroups } from '../../../../../utils/actionsDropdown';

export const useInactiveCycleContract = (contractId: string): IInactiveCycleContract => {
  const [isLoading, setIsLoading] = useState(false);
  const [cycleIdSettings, setCycleIdSettings] = useState<string>('');
  const [cycleNameSettings, setCycleNameSettings] = useState<string>('');
  const [cycleIdDelete, setCycleIdDelete] = useState<string>('');
  const [cycleIdAttach, setCycleIdAttach] = useState<string>('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSaveAttachment, setIsSaveAttachment] = useState<boolean>(false);
  const [cycleIdDuplicate, setCycleIdDuplicate] = useState<string>('');
  const [cycleNameDuplicate, setCycleNameDuplicate] = useState<string>('');

  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    cycles: {
      updateCycle,
      getInactiveCyclesByLocation,
      getCyclesCountByLocation,
      deleteCycle,
      saveAttachment,
      getAttachments,
      resetAttachments,
    },
  } = useAppDispatch();

  const ability = useAbilityContext();

  const [canDeleteCycle, canUpdateCycle, canUpdateCycleReport] = useMemo(() => {
    return [
      ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Update, PAGE_TITLE.CYCLE_REPORT),
    ];
  }, [ability]);

  const isChangeLoading = useRef(true);

  const fetchDataHandler = async (locationId: string) => {
    try {
      if (!locationId) {
        return;
      }

      if (isChangeLoading.current) {
        setIsLoading(true);
      }

      await getInactiveCyclesByLocation({ locationId });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isChangeLoading.current = false;
    }
  };

  const dataRefetch = async (locationId: string) => {
    await Promise.all([getCyclesCountByLocation({ locationId }), fetchDataHandler(locationId)]);
  };

  //cycle settings
  const openSelectedCycleSettings = useCallback((cycle: Cycle) => {
    setCycleIdSettings(cycle.id);
    setCycleNameSettings(cycle.name);
  }, []);

  const closeSelectedCycleSettings = useCallback(() => {
    setCycleIdSettings('');
    setCycleNameSettings('');
  }, []);

  //cycle delete
  const openSelectedCycleDelete = useCallback((cycleId: string) => {
    setCycleIdDelete(cycleId);
  }, []);

  const closeSelectedCycleDelete = useCallback(() => {
    setCycleIdDelete('');
  }, []);

  const onAcceptSelectedCycleDelete = useCallback(async () => {
    closeSelectedCycleDelete();

    await deleteCycle(cycleIdDelete);

    await dataRefetch(clientId);
  }, [cycleIdDelete, closeSelectedCycleDelete]);

  //attach to cycle
  const openSelectedCycleAttach = useCallback((cycleId: string) => {
    setCycleIdAttach(cycleId);
  }, []);

  const closeSelectedCycleAttach = useCallback(() => {
    setCycleIdAttach('');
  }, []);

  const onAcceptSelectedCycleAttach = useCallback(async () => {
    try {
      setIsSaveAttachment(true);
      await saveAttachment({ id: cycleIdAttach, attachment: attachment });
    } finally {
      setIsSaveAttachment(false);
    }
  }, [cycleIdAttach, attachment]);

  const onSelectFile = (file: File | null) => {
    setAttachment(file);
  };

  //multi-duplicate cycle
  const openSelectedCycleDuplicate = useCallback((cycleId: string, cycleName: string) => {
    setCycleIdDuplicate(cycleId);
    setCycleNameDuplicate(cycleName);
  }, []);

  const closeSelectedCycleDuplicate = useCallback(() => {
    setCycleIdDuplicate('');
    setCycleNameDuplicate('');
  }, []);

  const onCycleDuplicated = useCallback(() => {
    dataRefetch(clientId);
  }, [clientId]);

  const onActivateCycle = async (cycleId: string) => {
    await updateCycle({
      id: cycleId,
      status: CycleStatus.Active,
      start_at: new Date().getTime(),
    });

    await dataRefetch(clientId);
  };

  const editCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.edit'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.EDIT_CYCLE_WITHOUT_SCREEN, { clientId, contractId, id: cycleId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const deleteCycleFromScreenActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconBin fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.delete'),
      onClick: (cycleId: string) => {
        openSelectedCycleDelete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleDelete]);

  const attachToCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <Paperclip stroke="#000" size={ElementSize.XXS} />,
      name: t('commonText.viewAttachments'),
      onClick: (cycleId: string) => {
        openSelectedCycleAttach(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleAttach]);

  const openCycleReportActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <OpenReport stroke="#000" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.openReport'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, { id: cycleId, clientId, contractId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const activateCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => (
        <div className="flex h-4 items-center justify-center text-success">
          <IconSwitcher size={ElementSize.XXS} />
        </div>
      ),
      name: t('commonText.activate'),
      isPinned: true,
      onClick: async (cycleId: string) => {
        await onActivateCycle(cycleId);
      },
      loading: false,
    };
  }, [onActivateCycle]);

  const duplicateCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCopy fill="#000" size={ElementSize.XXS} />,
      name: 'Multi-duplicate',
      onClick: (cycleId: string, cycleData: Cycle) => {
        openSelectedCycleDuplicate(cycleId, cycleData.name);
      },
      loading: false,
    };
  }, [openSelectedCycleDuplicate]);

  const cycleActions: IActionsDropdown[] = useMemo(() => {
    // Activate is pinned - it renders as its own button next to the menu,
    // not as a row inside it, so it doesn't take part in the divider grouping.
    const pinnedActions: IActionsDropdown[] = canUpdateCycle ? [activateCycleActionBtn] : [];

    const viewGroup: IActionsDropdown[] = [
      ...(canUpdateCycle ? [attachToCycleActionBtn] : []),
      ...(canUpdateCycleReport ? [openCycleReportActionBtn] : []),
    ];

    const editGroup: IActionsDropdown[] = canUpdateCycle ? [editCycleActionBtn, duplicateCycleActionBtn] : [];

    const deleteGroup: IActionsDropdown[] = canDeleteCycle ? [deleteCycleFromScreenActionBtn] : [];

    return [...pinnedActions, ...joinActionGroups(viewGroup, editGroup, deleteGroup)];
  }, [
    //abilities
    canUpdateCycle,
    canDeleteCycle,
    canUpdateCycleReport,
    //btns
    editCycleActionBtn,
    deleteCycleFromScreenActionBtn,
    activateCycleActionBtn,
    attachToCycleActionBtn,
    openCycleReportActionBtn,
    duplicateCycleActionBtn,
  ]);

  useEffect(() => {
    if (cycleIdAttach) {
      getAttachments(cycleIdAttach);
    }

    return () => {
      resetAttachments();
    };
  }, [cycleIdAttach]);

  return {
    isLoading,
    cycleActions,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //attach to cycle
    cycleIdAttach,
    isSaveAttachment,
    closeSelectedCycleAttach,
    onAcceptSelectedCycleAttach,
    onSelectFile,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  };
};
